import { ClassNamesConfig, GroupBase } from 'react-select';

import { cn } from '../cn';

/**
 * Tailwind styling for react-select's `unstyled` mode, replacing the old
 * classNamePrefix + SCSS approach. Two recurring cascade gotchas show up
 * throughout this file, flagged inline only where they bite (the second
 * one no longer can, but explains why some values are written as they are):
 *
 * 1. react-select bakes a few properties (control `minHeight`, option
 *    `fontSize`/`display`) unconditionally, *outside* its own
 *    `unstyled ? {} : {...}` branches, as unlayered emotion CSS. Unlayered
 *    beats a normal-priority Tailwind utility regardless of source order,
 *    so those spots need the `!` important modifier.
 * 2. Bootstrap used to generate `!important` utilities under Tailwind's
 *    names (`border`, `shadow-sm`, `shadow-lg`), which beat Tailwind's
 *    despite its later layer, so this file spelled some values as
 *    arbitrary utilities (`shadow-[...]`). Bootstrap generates no utility
 *    any more (#478); the names are Tailwind's.
 *
 * Values throughout are pinned to the live pre-migration (Bootstrap/SCSS)
 * deployment via computed-style diffing, not eyeballed.
 */
export interface SelectTailwindConfigOptions {
  size?: 'sm';
  variant?: 'tableFilter' | 'tableCell';
  hasError?: boolean;
}

export const getSelectTailwindClassNames = <
  Option = unknown,
  IsMulti extends boolean = boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  options: SelectTailwindConfigOptions = {},
): ClassNamesConfig<Option, IsMulti, Group> => {
  const { size, variant, hasError = false } = options;
  const isSm = size === 'sm';
  const isTableFilter = variant === 'tableFilter';
  // Shorter control for selects embedded in a table row, so they don't
  // blow out the row height (K8sSecurityRulesField.tsx's protocol/
  // direction columns).
  const isTableCell = variant === 'tableCell';

  return {
    // `metronic-select-container`/`metronic-select__*` literal classes are
    // kept everywhere in this file alongside the Tailwind utilities:
    // LocalLogin.css, VStepperFormStep.scss, SavedFilterSelect.scss and
    // the E2E test helpers (waldur-integration-testing's
    // tests/components/select.py) all still hook into these names
    // directly, the same way `classNamePrefix` used to let them.
    container: () => 'metronic-select-container w-full text-sm relative',

    control: ({ isFocused, isDisabled }) => {
      const stateClasses = cn(
        'metronic-select__control',
        isFocused && 'metronic-select__control--is-focused',
        isDisabled && 'metronic-select__control--is-disabled',
      );
      return cn(
        stateClasses,
        // `font-normal`: a control must not inherit an ambient weight from its
        // host — `.card-header` sets `font-weight: 700`, which rendered the
        // value and placeholder bold in every card-toolbar select.
        'flex items-center justify-between w-full transition-colors font-normal',
        // 8px radius matches the old control's $border-radius (Tailwind's
        // `rounded-md` step is 6px).
        'rounded-lg border border-solid bg-primary',
        // Gotcha #1: `min-h-*!` overrides react-select's forced 38px.
        // Heights below match the old control's measured height per
        // variant; `px-[11px]` doubles as the search-icon-to-border gap
        // for `isTableFilter` (FilterSelectControl's icon only supplies
        // the icon-to-text gap, not icon-to-border — see components.tsx).
        isTableCell
          ? 'min-h-[35px]! m-[1px] px-[11px] py-[6px] text-sm'
          : isSm
            ? 'min-h-[28px]! px-[8px] py-[4.5px] text-sm'
            : 'min-h-[40px]! px-[11px] py-[6px] text-sm',
        hasError
          ? 'border-danger-subtle ring-1 ring-[var(--status-danger-border)]'
          : isFocused
            ? // --border-brand: the old focus color ($border-brand),
              // brand-300 in light and brand-400 in dark. No shadow: the old focused control has
              // exactly one box-shadow layer (this ring).
              'border-[var(--border-brand)] ring-1 ring-[var(--border-brand)]'
            : // eslint-disable-next-line waldur-custom/require-dark-pair -- a hover edge: brand-500 is 3.6:1 on the dark field, over the 3:1 non-text minimum
              'border-secondary hover:border-brand-500',
        // Flat disabled fill, not an opacity wash: matches the old
        // $input-disabled-bg, which dimmed only the placeholder text.
        isDisabled && 'bg-secondary cursor-not-allowed',
      );
    },

    valueContainer: ({ isMulti }) =>
      cn(
        'flex flex-1 items-center gap-[6px]',
        // A multi-select collapses everything past the second value into a
        // `+N` tag, so wrapping only ever bought a second line that made the
        // control change height as values came and went. Without wrapping the
        // 2px band that separated those lines has nothing to space, and it
        // pushed a 24px tag past the 26px the control holds inside its own
        // padding — growing the control by 2px the moment a value was picked.
        isMulti ? 'flex-nowrap' : 'flex-wrap',
      ),

    multiValue: () =>
      'bg-quaternary rounded-md px-[4px] py-[2px] flex items-center gap-[4px] m-[2px]',

    multiValueLabel: () => 'text-[11px] text-primary',

    multiValueRemove: () =>
      // eslint-disable-next-line waldur-custom/no-raw-status-ramp -- a hover tint one step stronger than -bg (error-100), so it reads on the chip
      'hover:bg-error-100 dark:hover:bg-error-900 hover:text-danger rounded-sm px-[2px] cursor-pointer transition-colors',

    singleValue: ({ isDisabled }) =>
      cn(
        // Literal class, same reasoning as `container`/`control`/etc above:
        // E2E tests (tests/pages/resources/vmware_vm/order.py and others)
        // assert against `.metronic-select__single-value` directly to read
        // back a single-select's displayed value.
        'metronic-select__single-value',
        'text-primary leading-normal truncate',
        isDisabled && 'text-quinary',
      ),

    placeholder: ({ isDisabled }) =>
      cn(
        'truncate select-none',
        // `--text-quaternary`, not `--text-quinary`: the old
        // select's placeholder and a plain Bootstrap `.form-control`'s
        // `::placeholder` render in the same color; `-muted` is a step
        // lighter and only matches elsewhere by coincidence.
        isDisabled ? 'text-[var(--text-disabled)]' : 'text-quaternary',
      ),

    input: () => 'text-primary m-0 p-0 text-[inherit]',

    indicatorsContainer: () => 'flex items-center gap-[4px] self-stretch',

    indicatorSeparator: () => 'hidden',

    dropdownIndicator: ({ selectProps: { menuIsOpen } }) =>
      cn(
        'text-quinary hover:text-quaternary transition-transform duration-200 p-[2px]',
        menuIsOpen && 'rotate-180',
        isTableFilter && 'hidden',
      ),

    clearIndicator: () =>
      cn(
        'metronic-select__clear-indicator text-quinary hover:text-danger cursor-pointer transition-colors',
        // Filter panels hide the dropdown arrow above, so the clear
        // button needs its own breathing room instead of sharing the
        // indicators gap.
        isTableFilter ? 'pr-[8px] mr-[8px]' : 'p-[2px]',
      ),

    menu: () =>
      cn(
        'metronic-select__menu',
        // Radius and shadow match the old menu's own values (a 1px outline plus a soft
        // 4px/11px drop shadow — the old menu has no real `border-width`
        // either, just this shadow).
        'z-[9999] mt-[8px] overflow-hidden rounded-lg border border-solid border-secondary',
        // `--bg-primary` matches the control's background in both themes.
        'bg-primary shadow-[0_0_0_1px_rgba(0,0,0,0.1),0_4px_11px_0_rgba(0,0,0,0.1)]',
      ),

    menuList: ({ isMulti }) =>
      cn(
        'metronic-select__menu-list',
        isMulti && 'metronic-select__menu-list--is-multi',
        // No `py-*`: the menu already clips to its own rounded corners
        // (`overflow-hidden` above), so an unpadded list lets the first/
        // last row's hover fill go flush to the border instead of
        // leaving a gap of bare menu background.
        'overflow-y-auto max-h-[260px]',
      ),

    option: ({ isFocused, isSelected, isDisabled, isMulti }) => {
      const stateClasses = cn(
        'metronic-select__option',
        isSelected && 'metronic-select__option--is-selected',
        isFocused && 'metronic-select__option--is-focused',
        isDisabled && 'metronic-select__option--is-disabled',
      );

      const colorClasses = cn(
        isSelected
          ? 'bg-primary-hover text-primary'
          : isFocused
            ? 'bg-primary-hover text-primary'
            : 'text-primary',
        isDisabled && 'opacity-40 cursor-not-allowed text-quinary',
      );

      // `flex!`/`text-[…]!` are gotcha #1: react-select's Option forces
      // `display: block` and `fontSize: inherit` unconditionally.
      // `justify-between` only for single-select: it pushes the row's
      // content away from the selected row's check (SelectOption's
      // `[data-select-check]`) on the far right. Multi-select renders two
      // real children (checkbox, label) that `justify-between` would shove
      // apart instead of keeping together — the old SCSS scoped this the
      // same way, to `:not(.metronic-select__menu-list--is-multi)` only.
      // The `grow` rule (also single-select only, as in the old SCSS) lets
      // a custom Option's content fill the row, so anything it right-aligns
      // (RoleOption's type badge, UserListOptionInline's icons) stays at the
      // far edge instead of collapsing onto the label; it and the table
      // filter's `line-clamp-2` skip the check, which keeps its own size.
      if (isTableFilter) {
        return cn(
          stateClasses,
          'flex! items-center gap-[8px] h-[40px] px-[12px] py-[2px]',
          !isMulti
            ? 'justify-between [&>*:not([data-select-check])]:grow [&>*:not([data-select-check])]:line-clamp-2'
            : '[&>label]:line-clamp-2',
          'cursor-pointer select-none transition-colors text-sm!',
          colorClasses,
        );
      }

      return cn(
        stateClasses,
        'flex! items-center gap-[8px] px-[16px] py-[10px] cursor-pointer select-none transition-colors text-sm!',
        // `gap-[8px]` keeps the content clear of the selected row's check.
        !isMulti && 'justify-between [&>*:not([data-select-check])]:grow',
        colorClasses,
      );
    },

    group: () => 'pt-[6px] pb-[2px]',
    groupHeading: () =>
      'px-[12px] py-[4px] text-[11px] font-semibold uppercase tracking-wider text-quinary',

    noOptionsMessage: () => 'p-[12px] text-center text-sm text-quinary',
    loadingMessage: () => 'p-[12px] text-center text-sm text-quinary',
  };
};
